import { motion, AnimatePresence } from 'framer-motion'
import { X, Copy, CheckCircle2, LogOut, Trash2, Plus, LayoutTemplate, Palette, Globe } from 'lucide-react'
import { useState, useRef } from 'react'
import { useAuthStore } from '../store/authStore'
import { useThemeStore, type ThemeMode, THEME_PALETTES } from '../store/themeStore'
import { useCardStore } from '../store/cardStore'
import { useNavigate } from 'react-router-dom'
import QRCode from 'react-qr-code'
import axios from 'axios'

interface SettingsModalProps {
    isOpen: boolean
    onClose: () => void
}

type Tab = 'Pages' | 'Theme' | 'Publish'

function normalizeThemeMode(value: unknown): ThemeMode {
    return value === 'mono' || value === 'glass' || value === 'neo' || value === 'wood'
        ? value
        : 'mono'
}

export default function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
    const [activeTab, setActiveTab] = useState<Tab>('Pages')

    // Auth & Routing
    const { user, setSubdomain, setVisibility, logout, deleteAccount } = useAuthStore()
    const navigate = useNavigate()

    // Stores
    const { theme, colorPalette, cardRadius, setColorPalette, setCardRadius } = useThemeStore()
    const { cards, updateCard, subpages, fetchSubpages } = useCardStore()
    const safeTheme = normalizeThemeMode(theme)

    // Pages States
    const [subdomainValue, setSubdomainValue] = useState(user?.subdomain || '')
    const [subdomainError, setSubdomainError] = useState('')
    const [subdomainLoading, setSubdomainLoading] = useState(false)
    const [newSlug, setNewSlug] = useState('')
    const [pageError, setPageError] = useState('')

    // Publish States
    const [copied, setCopied] = useState(false)
    const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false)
    const qrRef = useRef<HTMLDivElement>(null)

    if (!isOpen || !user) return null

    const persistProfileCardContent = async (pc: any, patch: Record<string, unknown>) => {
        const updatedContent = {
            ...(pc.content as any),
            ...patch,
        }

        const existsInCurrentCards = cards.some((c) => c.id === pc.id)
        if (existsInCurrentCards) {
            await updateCard(pc.id, { content: updatedContent })
        } else {
            await axios.put(`/api/cards/${pc.id}`, { content: updatedContent })
        }
    }

    // --- Handlers: Pages ---
    const handleSaveSubdomain = async () => {
        if (!subdomainValue.trim()) {
            setSubdomainError('Please enter a subdomain')
            return
        }
        setSubdomainLoading(true)
        setSubdomainError('')
        try {
            await setSubdomain(subdomainValue.trim().toLowerCase())
            setSubdomainError('Saved successfully')
            setTimeout(() => setSubdomainError(''), 2000)
        } catch (err: unknown) {
            const error = err as { response?: { data?: { error?: string } } }
            setSubdomainError(error.response?.data?.error || 'Failed to set subdomain')
        } finally {
            setSubdomainLoading(false)
        }
    }

    const handleDeleteAccount = async () => {
        const ok = window.confirm('Delete account permanently? This cannot be undone.')
        if (!ok) return
        try {
            await deleteAccount()
            navigate('/')
        } catch {
            setSubdomainError('Failed to delete account')
        }
    }

    // --- Handlers: Theme ---
    const handleThemeChange = async (newTheme: ThemeMode) => {
        const prev = useThemeStore.getState()
        const prevTheme = normalizeThemeMode(prev.theme)
        const nextPaletteSeed = prevTheme === newTheme ? prev.paletteSeed + 1 : 0

        useThemeStore.setState({ theme: newTheme, paletteSeed: nextPaletteSeed })
        localStorage.setItem('nok.theme', newTheme)
        document.documentElement.setAttribute('data-theme', newTheme)

        const profileCard = cards.find(c => c.card_type === 'profile')

        if (profileCard) {
            await persistProfileCardContent(profileCard, {
                theme: newTheme,
                paletteSeed: nextPaletteSeed,
                colorPalette,
            })
            return
        }
        try {
            const res = await axios.get('/api/cards?page_id=null')
            const list = (res.data?.cards || []) as any[]
            const pc = list.find((c) => c.card_type === 'profile')
            if (pc) {
                await persistProfileCardContent(pc, {
                    theme: newTheme,
                    paletteSeed: nextPaletteSeed,
                    colorPalette,
                })
            }
        } catch { /* ignore */ }
    }

    const handlePaletteChange = async (paletteIndex: number) => {
        setColorPalette(paletteIndex)
        document.documentElement.setAttribute('data-palette', String(paletteIndex))

        const profileCard = cards.find(c => c.card_type === 'profile')

        if (profileCard) {
            await persistProfileCardContent(profileCard, { colorPalette: paletteIndex })
            return
        }
        try {
            const res = await axios.get('/api/cards?page_id=null')
            const list = (res.data?.cards || []) as any[]
            const pc = list.find((c) => c.card_type === 'profile')
            if (pc) await persistProfileCardContent(pc, { colorPalette: paletteIndex })
        } catch { /* ignore */ }
    }

    const handleRadiusChange = async (r: 'sharp' | 'rounded') => {
        setCardRadius(r)
        const profileCard = cards.find(c => c.card_type === 'profile')

        if (profileCard) {
            await persistProfileCardContent(profileCard, { cardRadius: r })
            return
        }
        try {
            const res = await axios.get('/api/cards?page_id=null')
            const list = (res.data?.cards || []) as any[]
            const pc = list.find((c) => c.card_type === 'profile')
            if (pc) await persistProfileCardContent(pc, { cardRadius: r })
        } catch { /* ignore */ }
    }

    // --- Handlers: Publish ---
    const profileUrl = user.subdomain ? `https://${user.subdomain}.oda.bio` : ''
    const isPublic = user.is_public ?? true

    const handleCopy = async () => {
        if (!profileUrl) return
        try {
            await navigator.clipboard.writeText(profileUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy text: ', err)
        }
    }

    const handleToggleVisibility = async () => {
        try {
            setIsUpdatingVisibility(true)
            await setVisibility(!isPublic)
        } finally {
            setIsUpdatingVisibility(false)
        }
    }

    const handleExportQR = async () => {
        if (!qrRef.current) return
        try {
            const svgEl = qrRef.current.querySelector('svg')
            if (!svgEl) return

            const canvas = document.createElement('canvas')
            const scale = 4
            const size = 242 * scale
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')
            if (!ctx) return

            ctx.fillStyle = '#171717'
            ctx.fillRect(0, 0, size, size)

            const svgData = new XMLSerializer().serializeToString(svgEl)
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
            const svgUrl = URL.createObjectURL(svgBlob)

            const img = new Image()
            await new Promise((resolve, reject) => {
                img.onload = resolve
                img.onerror = reject
                img.src = svgUrl
            })
            URL.revokeObjectURL(svgUrl)

            const padding = 16 * scale
            ctx.drawImage(img, padding, padding, 210 * scale, 210 * scale)

            const boxSize = 82 * scale
            const boxX = (size - boxSize) / 2
            const boxY = (size - boxSize) / 2

            ctx.fillStyle = '#171717'
            ctx.fillRect(boxX, boxY, boxSize, boxSize)
            ctx.strokeStyle = 'rgba(255,255,255,0.6)'
            ctx.lineWidth = 1 * scale
            ctx.strokeRect(boxX + ctx.lineWidth / 2, boxY + ctx.lineWidth / 2, boxSize - ctx.lineWidth, boxSize - ctx.lineWidth)

            ctx.textAlign = 'left'
            ctx.textBaseline = 'top'

            if ('letterSpacing' in ctx) {
                ; (ctx as any).letterSpacing = '0.08em'
            }
            ctx.fillStyle = '#ffffff'
            ctx.font = `900 ${32 * scale}px Outfit, sans-serif`
            ctx.fillText('oda', boxX + 11 * scale, boxY + 18 * scale)

            if ('letterSpacing' in ctx) {
                ; (ctx as any).letterSpacing = '0.12em'
            }
            ctx.fillStyle = 'rgba(255,255,255,0.45)'
            ctx.font = `400 ${10 * scale}px Outfit, sans-serif`
            ctx.fillText('.bio', boxX + 11 * scale, boxY + 50 * scale)

            const url = canvas.toDataURL('image/png')
            const link = document.createElement('a')
            link.download = `${user.subdomain || 'profile'}-qr.png`
            link.href = url
            link.click()
        } catch (err) {
            console.error('Failed to export QR code', err)
        }
    }

    const handleLogout = async () => {
        await logout()
        navigate('/')
    }

    const themes: { id: ThemeMode; label: string; swatches: string[] }[] = [
        { id: 'mono', label: 'Default', swatches: ['#0a0a0a', '#fafafa'] },
        { id: 'glass', label: 'Glass', swatches: ['#e8ecf4', '#b4d2ff', '#dfe7ff'] },
        { id: 'neo', label: 'Neo', swatches: ['#011910', '#037d50', '#98FFB4'] },
        { id: 'wood', label: 'Wood', swatches: ['#241914', '#ee8695', '#00b9be'] },
    ]
    const currentThemePalettes = THEME_PALETTES[safeTheme] || THEME_PALETTES.mono

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="bg-[#0a0a0a] border border-[#262626] p-0 w-full max-w-sm max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        style={{ borderRadius: '4px' }}
                    >
                        {/* Header / Tabs */}
                        <div className="flex border-b border-[#262626] bg-[#0f0f0f]">
                            <button
                                onClick={() => setActiveTab('Pages')}
                                className={`flex-1 py-3 text-sm font-serif text-center flex items-center justify-center gap-2 transition-colors ${activeTab === 'Pages' ? 'text-white bg-[#171717]' : 'text-white/40 hover:text-white/70'
                                    }`}
                            >
                                <LayoutTemplate size={16} /> Pages
                            </button>
                            <button
                                onClick={() => setActiveTab('Theme')}
                                className={`flex-1 py-3 text-sm font-serif text-center flex items-center justify-center gap-2 border-l border-[#262626] transition-colors ${activeTab === 'Theme' ? 'text-white bg-[#171717]' : 'text-white/40 hover:text-white/70'
                                    }`}
                            >
                                <Palette size={16} /> Theme
                            </button>
                            <button
                                onClick={() => setActiveTab('Publish')}
                                className={`flex-1 py-3 text-sm font-serif text-center flex items-center justify-center gap-2 border-l border-[#262626] transition-colors ${activeTab === 'Publish' ? 'text-white bg-[#171717]' : 'text-white/40 hover:text-white/70'
                                    }`}
                            >
                                <Globe size={16} /> Publish
                            </button>
                            <button
                                onClick={onClose}
                                className="p-3 text-white/40 hover:text-white hover:bg-white/5 border-l border-[#262626] transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="p-6 overflow-y-auto flex-1 bg-[#0a0a0a]" style={{ minHeight: '300px' }}>

                            {/* --- TAB: PAGES --- */}
                            {activeTab === 'Pages' && (
                                <div className="flex flex-col gap-6">
                                    {/* Subdomain */}
                                    <div>
                                        <label className="block text-white/50 font-serif text-xs uppercase tracking-wider mb-2">Claim Your URL</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={subdomainValue}
                                                onChange={(e) => setSubdomainValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                                placeholder="username"
                                                className="flex-1 bg-[#121212] border border-[#262626] text-white px-3 py-2 font-serif focus:outline-none focus:border-[#404040]"
                                            />
                                            <span className="text-white/40 font-serif text-sm">.oda.bio</span>
                                        </div>
                                        {subdomainError && <p className="text-white/60 text-xs mt-2">{subdomainError}</p>}
                                        <button
                                            onClick={handleSaveSubdomain}
                                            disabled={subdomainLoading}
                                            className="mt-3 w-full py-2 bg-[#1f1f1f] border border-[#333] text-white/90 font-serif text-sm hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
                                        >
                                            {subdomainLoading ? 'Saving...' : 'Save URL'}
                                        </button>
                                    </div>

                                    {/* Subpages */}
                                    {user?.subdomain && (
                                        <div className="border-t border-[#262626] pt-5">
                                            <label className="block text-white/50 font-serif text-xs uppercase tracking-wider mb-3">Subpages</label>
                                            <div className="space-y-2 mb-3">
                                                <div className="px-3 py-2 bg-[#121212] border border-[#262626] text-white/40 font-serif text-sm">
                                                    /main
                                                </div>
                                                {subpages.map((sp) => (
                                                    <div key={sp.id} className="flex items-center gap-2 px-3 py-2 bg-[#121212] border border-[#262626] text-white/80 font-serif text-sm group">
                                                        <span className="flex-1 truncate">/{sp.slug}</span>
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await axios.delete(`/api/subpages/${sp.id}`)
                                                                    fetchSubpages()
                                                                } catch { /* ignore */ }
                                                            }}
                                                            className="text-red-400/40 hover:text-red-400/90 transition-colors opacity-0 group-hover:opacity-100"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            {subpages.length < 4 && (
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={newSlug}
                                                        onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                                        placeholder="new-page"
                                                        className="flex-1 bg-[#121212] border border-[#262626] text-white px-3 py-2 font-serif text-sm focus:outline-none focus:border-[#404040]"
                                                    />
                                                    <button
                                                        onClick={async () => {
                                                            if (!newSlug.trim()) return
                                                            setPageError('')
                                                            try {
                                                                await axios.post('/api/subpages', { slug: newSlug.trim() })
                                                                setNewSlug('')
                                                                fetchSubpages()
                                                            } catch (err: unknown) {
                                                                const e = err as { response?: { data?: { error?: string } } }
                                                                setPageError(e.response?.data?.error || 'Failed')
                                                            }
                                                        }}
                                                        className="px-3 py-2 bg-[#1f1f1f] border border-[#333] text-white/90 hover:bg-[#2a2a2a] transition-colors"
                                                    >
                                                        <Plus size={16} />
                                                    </button>
                                                </div>
                                            )}
                                            {pageError && <p className="text-red-400/80 text-xs mt-2">{pageError}</p>}
                                        </div>
                                    )}

                                    {/* Danger Zone */}
                                    <div className="border-t border-[#262626] pt-5 mt-auto">
                                        <button
                                            type="button"
                                            onClick={handleDeleteAccount}
                                            className="text-xs font-serif text-red-400/60 hover:text-red-400 transition-colors"
                                        >
                                            Delete Account Permanently
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* --- TAB: THEME --- */}
                            {activeTab === 'Theme' && (
                                <div className="flex flex-col gap-6">
                                    {/* Border Radius Toggle */}
                                    <div>
                                        <label className="block text-white/50 font-serif text-xs uppercase tracking-wider mb-3">Card Style</label>
                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => handleRadiusChange('sharp')}
                                                className={`flex-1 p-3 border flex flex-col items-center gap-2 transition-all ${cardRadius === 'sharp' ? 'bg-[#1f1f1f] border-white/40' : 'bg-[#121212] border-[#262626] hover:border-[#404040]'
                                                    }`}
                                            >
                                                <div className="w-8 h-8 border border-white/60 bg-white/5" />
                                                <span className="text-xs font-serif text-white/70">Sharp</span>
                                            </button>

                                            <button
                                                onClick={() => handleRadiusChange('rounded')}
                                                className={`flex-1 p-3 border flex flex-col items-center gap-2 transition-all ${cardRadius === 'rounded' ? 'bg-[#1f1f1f] border-white/40' : 'bg-[#121212] border-[#262626] hover:border-[#404040]'
                                                    }`}
                                            >
                                                <div className="w-8 h-8 border border-white/60 bg-white/5 rounded-[12px]" />
                                                <span className="text-xs font-serif text-white/70">Rounded</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Theme Mode */}
                                    <div>
                                        <label className="block text-white/50 font-serif text-xs uppercase tracking-wider mb-3">Theme Mode</label>
                                        <div className="flex items-center justify-between">
                                            {themes.map((t) => (
                                                <button
                                                    key={t.id}
                                                    type="button"
                                                    onClick={() => handleThemeChange(t.id)}
                                                    title={t.label}
                                                    className="w-10 h-10 rounded-full transition-transform"
                                                    style={{
                                                        background:
                                                            t.swatches.length === 2
                                                                ? `linear-gradient(135deg, ${t.swatches[0]} 0%, ${t.swatches[0]} 50%, ${t.swatches[1]} 50%, ${t.swatches[1]} 100%)`
                                                                : `linear-gradient(135deg, ${t.swatches[0]} 0%, ${t.swatches[0]} 33%, ${t.swatches[1]} 33%, ${t.swatches[1]} 66%, ${t.swatches[2]} 66%, ${t.swatches[2]} 100%)`,
                                                        boxShadow:
                                                            safeTheme === t.id
                                                                ? '0 0 0 2px rgba(250,250,250,0.95), 0 0 0 4px rgba(0,0,0,0.55)'
                                                                : '0 0 0 1px rgba(255,255,255,0.18)',
                                                        transform: safeTheme === t.id ? 'scale(1.1)' : 'scale(1)'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Color Palette */}
                                    {safeTheme !== 'mono' && (
                                        <div className="border-t border-[#262626] pt-5">
                                            <label className="block text-white/50 font-serif text-xs uppercase tracking-wider mb-3">Color Palette</label>
                                            <div className="flex items-center gap-4">
                                                {currentThemePalettes.map((palette, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        onClick={() => handlePaletteChange(index)}
                                                        title={palette.name}
                                                        className="w-8 h-8 rounded-full transition-transform"
                                                        style={{
                                                            background: palette.colors[0],
                                                            boxShadow:
                                                                colorPalette === index
                                                                    ? '0 0 0 2px rgba(250,250,250,0.95), 0 0 0 4px rgba(0,0,0,0.55)'
                                                                    : '0 0 0 1px rgba(255,255,255,0.18)',
                                                            transform: colorPalette === index ? 'scale(1.1)' : 'scale(1)'
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- TAB: PUBLISH --- */}
                            {activeTab === 'Publish' && (
                                <div className="flex flex-col gap-6">
                                    {!profileUrl ? (
                                        <div className="text-center py-8">
                                            <p className="text-white/50 text-sm font-serif">You must claim a URL in the Pages tab first.</p>
                                            <button onClick={() => setActiveTab('Pages')} className="mt-4 px-4 py-2 border border-[#404040] text-xs uppercase tracking-widest font-serif text-white/70 hover:bg-white/5 transition-colors">Go to Pages</button>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleToggleVisibility}
                                                disabled={isUpdatingVisibility}
                                                className={`w-full bg-[#121212] border border-[#262626] text-white px-4 py-3 font-serif flex items-center justify-between transition-colors hover:border-[#404040] ${isUpdatingVisibility ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <span className="text-sm text-white/80">Public Profile</span>
                                                <div className={`w-10 h-5 border rounded-full flex items-center px-0.5 transition-colors ${isPublic ? 'border-green-500/50 bg-green-500/10' : 'border-[#404040] bg-[#1a1a1a]'
                                                    }`}>
                                                    <div className={`w-3.5 h-3.5 rounded-full transition-all ${isPublic ? 'bg-green-400 translate-x-5' : 'bg-[#555] translate-x-0'
                                                        }`} />
                                                </div>
                                            </button>

                                            <div className="flex items-center gap-2 bg-[#121212] border border-[#262626] p-1 shadow-inner">
                                                <div className="flex-1 px-3 py-2 overflow-x-auto whitespace-nowrap scrollbar-hide text-white/70 font-serif text-sm">
                                                    {profileUrl}
                                                </div>
                                                <button
                                                    onClick={handleCopy}
                                                    className="flex items-center justify-center w-10 h-10 border-l border-[#262626] hover:bg-white/5 text-white/80 transition-colors shrink-0"
                                                    title="Copy link"
                                                >
                                                    {copied ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} />}
                                                </button>
                                            </div>

                                            <div className="flex flex-col items-center gap-4 border border-[#262626] bg-[#0d0d0d] p-4">
                                                <div
                                                    ref={qrRef}
                                                    style={{
                                                        position: 'relative',
                                                        backgroundColor: '#171717',
                                                        padding: '16px',
                                                        width: '242px',
                                                        height: '242px',
                                                        margin: '0 auto',
                                                    }}
                                                >
                                                    <QRCode
                                                        value={profileUrl}
                                                        size={210}
                                                        bgColor="transparent"
                                                        fgColor="#ffffff"
                                                        level="H"
                                                    />
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            marginTop: '-41px',
                                                            marginLeft: '-41px',
                                                            width: '82px',
                                                            height: '82px',
                                                            backgroundColor: '#171717',
                                                            border: '1px solid rgba(255,255,255,0.6)',
                                                            boxSizing: 'border-box',
                                                            paddingTop: '18px',
                                                            paddingLeft: '11px',
                                                            pointerEvents: 'none',
                                                        }}
                                                    >
                                                        <div style={{ color: '#ffffff', fontSize: '32px', fontWeight: 900, letterSpacing: '0.08em', fontFamily: 'Outfit, sans-serif', lineHeight: 1, margin: 0, padding: 0 }}>
                                                            oda
                                                        </div>
                                                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', letterSpacing: '0.12em', fontFamily: 'Outfit, sans-serif', lineHeight: 1, margin: 0, padding: 0, marginTop: '-2px' }}>
                                                            .bio
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={handleExportQR}
                                                    className="w-full py-2 bg-[#1f1f1f] text-white/80 font-serif text-xs hover:bg-[#2a2a2a] transition-colors uppercase tracking-widest border border-[#333]"
                                                >
                                                    Export QR Code
                                                </button>
                                            </div>

                                            <div className="pt-2">
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full flex items-center justify-center gap-2 py-3 border border-[#262626] text-red-400/80 hover:border-red-400/40 hover:bg-red-400/10 font-serif text-sm transition-colors"
                                                >
                                                    <LogOut size={16} />
                                                    <span>Log out</span>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
